import { useId, useState, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';

import useIsPWA from '@/hooks/use-is-pwa';
import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value'> {
    name: string;
    label?: ReactNode;
    value?: string | number | null;
    error?: FieldErrorLike;
    /** Ignoré : conservé pour les appelants qui le passent encore, la valeur passe par le contexte du formulaire. */
    setValue?: unknown;
}

/** Liste déroulante à libellé flottant, branchée sur le formulaire react-hook-form parent. */
const Select = (props: SelectProps) => {
    const { value, error, children, label, required, name, onChange: customOnChange, onBlur: customOnBlur, className, setValue: _sv, ...rest } = props;
    void _sv;
    const { setValue } = useFormContext();
    const id = useId();
    const errorId = `${id}-error`;
    const [focused, setFocused] = useState(false);
    const isPWA = useIsPWA();

    return (
        <div className="relative w-full">
            <select
                id={id}
                name={name}
                required={required}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                value={value ?? ''}
                onChange={customOnChange || ((e) => setValue(name, e.target.value, { shouldValidate: true }))}
                onBlur={(e) => {
                    setFocused(false);
                    customOnBlur?.(e);
                }}
                onFocus={() => setFocused(true)}
                className={`w-full px-6 py-4 rounded-lg border border-border bg-card text-base transition-colors appearance-none cursor-pointer placeholder-transparent focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-destructive focus:ring-destructive/20 focus:border-destructive' : ''} ${!value ? 'text-muted-foreground' : ''} ${className || ''}`}
                {...rest}
            >
                {children}
            </select>
            {label && (
                <label
                    htmlFor={id}
                    className={`absolute left-3 ${isPWA ? 'bg-white' : 'bg-card'} px-1 transition-all duration-200 pointer-events-none text-xs -top-2 ${focused || value ? 'text-primary' : 'text-muted-foreground'} ${error ? 'text-destructive' : ''}`}
                >
                    {label}
                    {required ? (
                        <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                            *
                        </sup>
                    ) : (
                        ''
                    )}
                </label>
            )}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
                <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
            {error && (
                <p id={errorId} role="alert" aria-live="assertive" className="text-xs text-destructive italic mt-1">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default Select;
